import React, { useEffect, useState } from 'react';
import { CheckCircle, Loader2, Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import SettingsShell from './SettingsShell';
import useUnsavedChanges from '../../hooks/useUnsavedChanges';

const AccountSettings = () => {
  const { user, updateUser, logout } = useAuth();
  const [fullName, setFullName] = useState(user?.fullName || '');
  const [businessName, setBusinessName] = useState(user?.businessName || '');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setFullName(user?.fullName || '');
  }, [user?.fullName]);
  useEffect(() => { setBusinessName(user?.businessName || ''); }, [user?.businessName]);

  const baselineName = user?.fullName || '';
  const baselineBusiness = user?.businessName || '';
  const isDirty = fullName.trim() !== baselineName.trim() || businessName.trim() !== baselineBusiness.trim();
  useUnsavedChanges(isDirty);

  const handleSave = async (event) => {
    event.preventDefault();
    const trimmed = fullName.trim();
    if (!trimmed) {
      setError('Full name cannot be empty');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      await updateUser({ fullName: trimmed, ...(businessName.trim() !== baselineBusiness.trim() ? { businessName: businessName.trim() } : {}) });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2500);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to update profile';
      setError(typeof msg === 'string' ? msg : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsShell
      title="Account"
      description="Update your personal details and login info."
    >
      {success && (
        <div role="status" className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-700 text-sm flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          Saved
        </div>
      )}
      {error && (
        <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSave} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm space-y-4">
        <div>
          <label htmlFor="account-full-name" className="block text-sm font-medium text-gray-700 mb-1">
            Full Name
          </label>
          <input
            id="account-full-name"
            type="text"
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-gray-900 font-medium transition-all"
            placeholder="e.g. Tendai Moyo"
          />
        </div>

        <div>
          <label htmlFor="account-business-name" className="block text-sm font-medium text-gray-700 mb-1">Profile business name (optional)</label>
          <input id="account-business-name" maxLength={120} value={businessName} onChange={event => setBusinessName(event.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm" aria-describedby="account-business-name-help" />
          <p id="account-business-name-help" className="mt-2 text-sm text-gray-500">Saved on your personal profile and used in some emails. Your team’s shared workspace name is managed in Workspace overview.</p>
        </div>

        <div>
          <label htmlFor="account-email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              id="account-email"
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500 font-medium cursor-not-allowed"
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Email changes are currently managed by support.
          </p>
        </div>

        <button
          type="submit"
          disabled={saving || !isDirty}
          className="w-full bg-primary-600 text-white p-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-primary-700 active:bg-primary-800 transition-all disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Account Changes'
          )}
        </button>
      </form>

      <div className="pt-2">
        <button
          type="button"
          onClick={logout}
          className="w-full bg-white border border-gray-300 text-gray-700 p-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-gray-50 active:bg-gray-100 transition-all"
        >
          Logout
        </button>
      </div>
    </SettingsShell>
  );
};

export default AccountSettings;
